'use client'

import React, { useEffect, useState } from 'react'
import Breadcrumbs from '../Breadcrumbs'
import {
  ButtonGroup as ButtonGroupProps,
  ContactVendorButton as ContactVendorButtonType,
} from '@/payload-types'
import { Icon } from '@/ui/atoms/Icon'
import { CMSButtonGroup } from '../CMSButtonGroup'
import { ContactVendorButton } from '../ContactVendorButton'
import FavouriteButton from '@/ui/components/FavouriteButton'
import { usePathname } from 'next/navigation'
import { removeLangFromPath } from '@/utils/removeLangFromPath'
import classNames from 'classnames'
import { MaxWidthGutter } from '@/ui/layout/Gutter/MaxWidthGutter'

export interface BreadcrumbsNavigationProps {
  showFavourite?: boolean | null
  showShare?: boolean | null
  showContactVendor?: boolean | null
  buttons?: ButtonGroupProps
  areaSlugs?: string[]
  contactVendorButtonData?: ContactVendorButtonType
  breadCrumbTitle?: string | null
}

const getAppearanceForPath = (path: string): 'whiteBlur' | 'default' => {
  if (
    path.startsWith('/dashboard') ||
    // workflow hub pages: /workflows/<domain>/<workflow> (P2 masthead; the handbook templates
    // open on a white title block and keep the default bar, docs/style-guide)
    (path.startsWith('/workflows/') && path.split('/').length === 4 && process.env.NEXT_PUBLIC_STYLE_GUIDE !== '1')
  ) {
    return 'whiteBlur'
  }
  return 'default'
}

const BreadcrumbsNavigation: React.FC<BreadcrumbsNavigationProps> = ({
  showFavourite,
  showShare = false,
  showContactVendor,
  buttons,
  areaSlugs = [],
  contactVendorButtonData,
  breadCrumbTitle,
}) => {
  const [isScrolled, setIsScrolled] = useState(false)
  const pathName = usePathname()
  const currentAppearance = getAppearanceForPath(removeLangFromPath(pathName))

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      setIsScrolled(currentScrollY > 100)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  // Define both styles
  const whiteBlurStyle =
    'bg-bg-base-transparent-10 backdrop-blur border-border-subtle-transparent-10'
  const defaultStyle = 'border border-[#DCE9F9] bg-white bg-opacity-80 backdrop-blur'

  // For matching paths, start with whiteBlur and switch to default on scroll
  const appearance = (() => {
    if (currentAppearance === 'whiteBlur') {
      return isScrolled ? defaultStyle : whiteBlurStyle
    }
    return defaultStyle
  })()

  const isWhiteBlurStyle = appearance === whiteBlurStyle

  const hasActions = showFavourite || showShare || showContactVendor || (buttons?.length ?? 0) > 0

  return (
    <div className={classNames('h-8 border rounded-md transition-all duration-300', appearance)}>
      <div
        className={`
          h-full
          overflow-x-auto
          scrollbar-hide
          touch-pan-x
          relative
          -webkit-overflow-scrolling-touch
        `}
      >
        <div className="flex items-center justify-between h-full min-w-fit px-2">
          <div className="flex items-center h-full">
            <Breadcrumbs
              separator={
                <span className={`flex items-center me-2 flex-shrink-0`}>
                  <Icon
                    name={'arrow-right-s-line'}
                    className={isWhiteBlurStyle ? 'text-white' : undefined}
                  ></Icon>
                </span>
              }
              activeClasses={`text-text-base text-body-XS-medium whitespace-nowrap`}
              containerClasses="flex items-center h-full"
              listClasses={classNames('me-1 text-body-XS-medium whitespace-nowrap', {
                'text-[#636A79]': !isWhiteBlurStyle,
                'text-white': isWhiteBlurStyle,
              })}
              capitalizeLinks
              areaSlugs={areaSlugs}
              title={breadCrumbTitle}
            />
          </div>
          {hasActions && (
            <div className={`flex items-center gap-2 flex-shrink-0`}>
              {(showFavourite || showShare) && (
                <div
                  className={classNames('flex gap-2 px-2', {
                    'border-r border-border-subtle text-text-faded': !isWhiteBlurStyle,
                    'border-r border-white border-opacity-20 text-text-inverted': isWhiteBlurStyle,
                  })}
                >
                  {showFavourite && <FavouriteButton showBackground={false} />}
                  {/* {showShare && <ShareButton></ShareButton>} */}
                </div>
              )}
              {showContactVendor && contactVendorButtonData && (
                <ContactVendorButton
                  appearance={isWhiteBlurStyle ? 'ghostInverted' : 'ghost'}
                  buttonData={contactVendorButtonData}
                />
              )}
              <CMSButtonGroup
                buttons={buttons}
                fixedAppearance={isWhiteBlurStyle ? 'ghostInverted' : 'ghost'}
                fixedSize="mini"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BreadcrumbsNavigation
